import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import WorkCard from '@/components/feature/WorkCard';
import { useSeo } from '@/hooks/useSeo';
import { latestWorks, works } from '@/mocks/catalog';
import { newsList } from '@/mocks/news';

type Tab = 'movie' | 'news';

const suggest = ['悬疑', '高分纪录片', '日常', '美食', '科幻', '日本', '影评'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = (params.get('q') || '').trim();
  const tab = (params.get('tab') === 'news' ? 'news' : 'movie') as Tab;
  const [input, setInput] = useState(keyword);

  useSeo({
    title: keyword ? `“${keyword}” 搜索结果 - 91大神影视平台` : '站内搜索 - 91大神影视平台',
    description: keyword
      ? `在 91大神影视平台搜索“${keyword}”，查看相关的影视作品与影视资讯结果。`
      : '在 91大神影视平台搜索电影、剧集、综艺与影视资讯。',
    keywords: `91大神, 站内搜索, ${keyword || '影视搜索'}`,
    canonicalPath: '/search',
  });

  const lower = keyword.toLowerCase();
  const matchedWorks = keyword
    ? works.filter((work) =>
        [work.title, work.titleEn, work.director, work.region, work.year, ...work.genres, ...work.tags, ...work.cast]
          .join(' ')
          .toLowerCase()
          .includes(lower),
      )
    : [];
  const matchedNews = keyword
    ? newsList.filter((news) =>
        [news.title, news.summary, news.category, news.author, ...news.body].join(' ').toLowerCase().includes(lower),
      )
    : [];

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set('q', input.trim());
    next.set('tab', tab);
    setParams(next);
  };

  const switchTab = (nextTab: Tab) => {
    const next = new URLSearchParams(params);
    next.set('tab', nextTab);
    setParams(next);
  };

  const results = tab === 'movie' ? matchedWorks : matchedNews;

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-800">搜索结果</span>
          </nav>

          <h1 className="mt-3 font-display text-xl font-bold text-foreground-950 md:text-2xl">
            {keyword ? `“${keyword}” 的搜索结果` : '搜索影视资源'}
          </h1>

          <form onSubmit={submit} className="mt-4 flex flex-col gap-2 sm:flex-row">
            <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300/70 bg-background-100 pl-4 pr-3">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入片名、演员、题材或资讯关键词"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button type="button" onClick={() => setInput('')} aria-label="清空" className="text-foreground-400 hover:text-foreground-700">
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-400">热门搜索</span>
            {suggest.map((word) => (
              <Link
                key={word}
                to={`/search?q=${encodeURIComponent(word)}&tab=movie`}
                className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:bg-accent-100 hover:text-accent-800"
              >
                {word}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-background-300/60 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => switchTab('movie')}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                tab === 'movie' ? 'bg-primary-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              影视作品 ({matchedWorks.length})
            </button>
            <button
              type="button"
              onClick={() => switchTab('news')}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                tab === 'news' ? 'bg-primary-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              影视资讯 ({matchedNews.length})
            </button>
          </div>
          <p className="ml-auto font-label text-xs text-foreground-500">
            {keyword ? (
              <>
                共找到 <span className="font-bold text-foreground-950">{results.length}</span> 条{tab === 'movie' ? '影视' : '资讯'}结果
              </>
            ) : (
              '输入关键词开始搜索'
            )}
          </p>
        </div>

        {!keyword ? (
          <section className="mt-6">
            <h2 className="font-display text-lg font-bold text-foreground-950">热门推荐</h2>
            <p className="mt-1 text-xs text-foreground-500">先看看大家都在关注的内容</p>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {latestWorks.slice(0, 5).map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </section>
        ) : results.length === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-xl"></i>
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-950">没有找到相关内容</p>
            <p className="mt-1.5 max-w-md text-sm text-foreground-500">
              试试更短的片名、题材词（如「悬疑」「纪录片」）或地区，也可以通过分类页的筛选条件浏览。
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
              <button
                type="button"
                onClick={() => switchTab(tab === 'movie' ? 'news' : 'movie')}
                className="whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                在{tab === 'movie' ? '影视资讯' : '影视作品'}中查找
              </button>
              <Link
                to="/category/movie"
                className="whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                按条件浏览
              </Link>
            </div>
            <div className="mt-8 w-full max-w-4xl text-left">
              <h2 className="font-display text-base font-bold text-foreground-950">也许你会喜欢</h2>
              <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                {latestWorks.slice(0, 4).map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            </div>
          </div>
        ) : tab === 'movie' ? (
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
            {matchedWorks.map((work) => (
              <WorkCard key={work.id} work={work} showPlayCount />
            ))}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {matchedNews.map((news) => (
              <Link
                key={news.id}
                to={`/news/${news.id}`}
                className="group flex flex-col gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-accent-300 sm:flex-row"
              >
                <div className="h-[150px] w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[112px] sm:w-44">
                  <img
                    src={news.cover}
                    alt={news.title}
                    title={`${news.title} - 搜索结果`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">{news.category}</span>
                  <h2 className="mt-1.5 font-display text-base font-bold leading-snug text-foreground-950 group-hover:text-accent-700">
                    {news.title}
                  </h2>
                  <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">{news.summary}</p>
                  <p className="mt-2 flex items-center gap-3 text-[11px] text-foreground-400">
                    <span>{news.author}</span>
                    <span>{news.date}</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}